import { useState, useEffect } from "react";
import ResponsiveContainer from "../../../../layouts/ResponsiveContainer";
import "../../../../index.css";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { getOptions } from "../../../../redux/slices/indicatorDisplay/SearchAndFilter/filterAndSearchThunk";
import PredictiveSearchBar from "../../../../components/SearchBar/PredictiveSearchBar";
import { getIndicators, GetIndicatorsQuery } from "../../../../redux/slices/indicatorDisplay/indicator/indicatorThunk";
import Dropdown from "../../../../components/Inputs/Selects/CustomSelect";
import { setSelectedPeriod } from "../../../../redux/slices/periods/periodsSlice";
import useUrlParams from "../../../../hooks/useUrlParams";
import { idsFromUrl, idsToQuery } from "../../utils/filterParams";
import { useFilterDraft } from "../../hooks/useFilterDraft";
import { useIndicatorSearch } from "../../hooks/useIndicatorSearch";
import { useAppliedFilters } from "../../hooks/useAppliedFilters";
import Button from "../../../../components/Buttons/Button";
import ConfigFilterModal from "./ConfigFilterModal";
import FilterApplied from "./FilterApplied";
import { MdAutorenew, MdOutlineFilterAlt } from "react-icons/md";

const SearchAndFilter = () => {
    // ── Router / Redux ─────────────────────────────────────────────────
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { getParam, getMultipleParams, updateParams } = useUrlParams();

    const {
        rights,
        searchOfIndicators,
        loadingSearch,
        errorSearch,
        loadingOptions
    } = useAppSelector((state) => state.indicator_display_filter);

    const { periods } = useAppSelector((state) => state.periods);

    const params = new URLSearchParams(location.search);
    const periodParam = params.get("period") || "";
    const currentRights = getMultipleParams("rightId");

    // ── Estado local ───────────────────────────────────────────────────
    const [period, setPeriod] = useState<string>("");
    const [isConfigModalVisible, setIsConfigModalVisible] = useState<boolean>(false);

    const fetchIndicators = (query: Partial<GetIndicatorsQuery>) => dispatch(getIndicators({ endPeriod: Number(period), page: 1, limit: 5, ...query }));

    // Borrador del modal
    const { draft, updateDraft, resetDraft, cleanDraft, setDraftField, toggleDraftField, hasDraftFilters } = useFilterDraft();

    // Buscador
    const {
        searchValue,
        open,
        setOpen,
        error,
        setError,
        cleanValue,
        onChangeSearch,
        selectSearch,
        onSearchExpanded,
        onCleanValueSearch,
        clearSearchInput,
    } = useIndicatorSearch({ period, fetchIndicators, resetDraft });

    // Filtros aplicados (URL)
    const {
        appliedGroups,
        hasCleanableFilters,
        canApplyDraft,
        applyFilter,
        handleClean,
        removeAppliedFilter,
        loadUrlIntoDraft,
    } = useAppliedFilters({ draft, updateDraft, resetDraft, searchValue, clearSearchInput, fetchIndicators });

    // ── Effects ────────────────────────────────────────────────────────
    useEffect(() => {
        if (periodParam) {
            dispatch(getOptions({ period: periodParam }));
            setPeriod(periodParam);
            dispatch(setSelectedPeriod({ periodParam }));
        }
    }, [periodParam, dispatch]);

    // ── Period ─────────────────────────────────────────────────────────
    const handlePeriod = (period: string) => {
        if (!period) return;
        const year = period.split(" ").pop() || "";
        dispatch(setSelectedPeriod({ period: year }));
        setPeriod(year);

        const hasSearchMode = params.get("searchMode");
        const hasFilterApplied = getParam("filterApplied") === "true";

        if (hasSearchMode || hasFilterApplied) {
            fetchIndicators({ ...idsToQuery(idsFromUrl(getMultipleParams)), search: searchValue, endPeriod: Number(year) });
        } else if (currentRights.length) {
            fetchIndicators({ rightsId: currentRights, endPeriod: Number(year) });
        }

        updateParams({ period: year })
    };

    // ── Modal ──────────────────────────────────────────────────────────
    const openConfigModal = () => {
        loadUrlIntoDraft();
        setIsConfigModalVisible(true);
    };

    const closeConfigModal = () => setIsConfigModalVisible(false);

    const hasOptions = rights.length > 0;
    const isLoadingOptions = loadingOptions === 'pending';

    return (
        <ResponsiveContainer className="md:flex md:flex-col md:items-end">
            <Dropdown
                options={periods ?? []}
                onSelect={handlePeriod}
                selectedItem={`Período ${period}`}
                defaultSelectedItem={`Período ${period}`}
                label=""
                showLabel={false}
                className="min-w-[175px] max-w-[175px]"
            />
            <div className="bg-primary-100 rounded-md shadow-custom-dual flex flex-col mt-5 w-full">
                <div className="p-4">
                    <PredictiveSearchBar
                        placeholder="Buscar indicador"
                        isLoading={loadingSearch}
                        searchResults={searchOfIndicators ? searchOfIndicators : []}
                        searchValue={searchValue}
                        isOpen={open}
                        onChange={onChangeSearch}
                        onSearch={onSearchExpanded}
                        searchErrorMessage={errorSearch ? errorSearch : ""}
                        generalErrorMessage="Solo se permiten letras, números y los caracteres . ,"
                        hasError={error}
                        setHasError={setError}
                        selectSearch={selectSearch}
                        onCleanValue={onCleanValueSearch}
                        cleanValue={cleanValue}
                        setIsOpen={setOpen}
                    />
                </div>
                {hasOptions && (
                    <>
                        <div className="border w-full border-system-light-30"></div>
                        <div className="p-4">
                            <FilterApplied
                                groups={appliedGroups}
                                toogleConfigModal={openConfigModal}
                                onRemove={removeAppliedFilter}
                                disabledRemove={isLoadingOptions}
                            />
                            <div className='flex items-center justify-between sm:justify-end mt-6 gap-4'>
                                <Button
                                    label='Limpiar'
                                    type='button'
                                    showIconLeft={false}
                                    showIconRight={true}
                                    className='text-primary-40 py-1 md:py-0 !w-full sm:!w-max'
                                    onClick={handleClean}
                                    disabled={!hasCleanableFilters || isLoadingOptions}
                                    variant='outline-green'
                                    icon={MdAutorenew}
                                    iconSize={18}
                                />
                                <Button
                                    label='Configurar'
                                    type='button'
                                    showIconLeft={false}
                                    showIconRight={true}
                                    variant='terciary'
                                    onClick={openConfigModal}
                                    disabled={isLoadingOptions}
                                    icon={MdOutlineFilterAlt}
                                    iconSize={18}
                                    className="py-1 md:py-0 !w-full sm:!w-max"
                                />
                            </div>
                        </div>
                    </>)}
            </div>
            <ConfigFilterModal
                isOpen={isConfigModalVisible}
                toggle={closeConfigModal}
                draft={draft}
                toggleField={toggleDraftField}
                setField={setDraftField}
                disabledApply={!canApplyDraft}
                disabledClean={!hasDraftFilters}
                onApply={applyFilter}
                onClean={cleanDraft}
            />
        </ResponsiveContainer>
    );
};

export default SearchAndFilter;